import { Carousel, useCarouselContext } from '@ark-ui/react/carousel'
import './tcar.css'
import { CornerBrackets } from './CornerBrackets'

// Thumbnails carousel — behavior from the 21st.dev component (anubra266, built on
// Ark UI headless), restyled for the Orbital Ops dark theme: darkened imagery,
// bracket frame, mono caption, sapphire active thumb, HUD arrows. No autoplay
// (motion only on user action — DESIGN.md §6).
// `focus` = object-position for the crop (photos come in mixed aspect ratios, the stage is 16:9);
// `raw` skips the photo darkening (for ARGO's own renders, already graded for the dark page).
export type TItem = { src: string; caption: string; index: string; focus?: string; raw?: boolean }

// Stage-level overlay (scrim + brackets + caption) reading the current page from
// Ark context, so the caption always sits at the stage bottom and tracks slides.
function StageOverlay({ items }: { items: TItem[] }) {
  const api = useCarouselContext()
  const idx = Math.min(items.length - 1, Math.max(0, api.page ?? 0))
  const it = items[idx]
  return (
    <>
      <div className="tcar-scrim" aria-hidden />
      <CornerBrackets size={16} />
      <div className="tcar-cap">
        <span className="tcar-cap-n mono">{it.index}</span>
        <span className="tcar-cap-text">{it.caption}</span>
      </div>
    </>
  )
}

export function ThumbnailsCarousel({ items }: { items: TItem[] }) {
  return (
    <Carousel.Root slideCount={items.length} defaultPage={0} loop allowMouseDrag className="tcar">
      <div className="tcar-stage">
        <Carousel.ItemGroup className="tcar-track">
          {items.map((it, i) => (
            <Carousel.Item key={i} index={i} className="tcar-item">
              <img
                className={`tcar-img${it.raw ? ' tcar-img--raw' : ''}`}
                src={it.src}
                alt={it.caption}
                loading="lazy"
                decoding="async"
                style={it.focus ? { objectPosition: it.focus } : undefined}
              />
            </Carousel.Item>
          ))}
        </Carousel.ItemGroup>

        <StageOverlay items={items} />

        <Carousel.PrevTrigger className="tcar-arrow tcar-arrow--prev" aria-label="Trước / Previous">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
        </Carousel.PrevTrigger>
        <Carousel.NextTrigger className="tcar-arrow tcar-arrow--next" aria-label="Sau / Next">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
        </Carousel.NextTrigger>
      </div>

      <Carousel.IndicatorGroup className="tcar-thumbs">
        {items.map((it, i) => (
          <Carousel.Indicator key={i} index={i} className="tcar-thumb" aria-label={it.caption}>
            <img src={it.src} alt="" loading="lazy" decoding="async" style={it.focus ? { objectPosition: it.focus } : undefined} />
          </Carousel.Indicator>
        ))}
      </Carousel.IndicatorGroup>
    </Carousel.Root>
  )
}
